import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProduccionScanInput from './ProduccionScanInput';
import { produccionApi, errorMessage } from '../utils/produccionApi';
import { parseKg, formatKg, formatDate, parseLocation } from '../utils/produccion';
import { playTone } from '../utils/scanTone';
import useWideLayout from '../utils/useWideLayout';
import '../styles/produccion.css';

const REASONS = [
  { key: 'no_esta', label: 'No está' },
  { key: 'danado', label: 'Dañado' },
  { key: 'otro', label: 'Otro' },
];
const LINE_STATUS = {
  pendiente: { label: 'Pendiente', cls: '' },
  completa: { label: 'Completa', cls: 'green' },
  corta: { label: 'Corta', cls: 'amber' },
};
const CLOSED_STAGES = {
  anulada: 'Esta orden está anulada.',
  antigua: 'Las órdenes antiguas no se retiran desde esta pantalla.',
  terminada: 'Esta orden ya está terminada.',
  finalizada: 'Esta orden ya está cerrada.',
};
// Stages where someone is retiring: entering takes the order.
const OPEN_STAGES = ['por_retirar', 'retirando'];
const DUPLICATE = 'Esta etiqueta ya se escaneó en esta orden. Si es otra pieza con el mismo peso, tipea los kg.';
const BAD_KG = 'Escribe los kg, por ejemplo 4,2.';
const SAVED = 'El retiro se guardó.';
const NOTHING_PULLED = 'No se retiró materia prima. Avisa al supervisor.';
const NOTICE_CLASS = { error: 'prod-bad prod-error', warn: 'prod-warn', ok: 'prod-ok' };
// Errors that only a fresh look at the order can get past.
const RELOAD_CODES = ['lote_cambio', 'linea', 'estado'];
// No single piece weighs a tonne: a number that big is a barcode shot into the kg field.
const MAX_PIECE_KG = 1000;

const round3 = (n) => Math.round(n * 1000) / 1000;
const sumKg = (pieces) => round3(pieces.reduce((sum, p) => sum + Number(p.kg), 0));
const placeOf = (lot) => `${lot.rack}-${lot.shelf}`;
const expiry = (date) => (date ? `vence ${formatDate(date)}` : 'sin vencimiento');
const pulledOf = (state, lineId) => {
  const line = state && state.lines.find((l) => l.id === lineId);
  return line ? Number(line.pulled) : 0;
};

// One id per batch of pieces: if "Listo" has to be pressed again after a lost
// answer, the server recognizes the pull it already saved.
function newRequestId() {
  const { crypto } = window;
  if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// The lot the screen is working on. When it changes, the steps start over.
function lotKey(state) {
  const current = state && state.current;
  if (!current) return 'none';
  const { target } = current;
  return target ? `${current.line_id}|${target.rack}|${target.shelf}|${target.batch}` : `${current.line_id}|-`;
}

function RetiroPanel({ state }) {
  const { lines, current } = state;
  const currentId = current ? current.line_id : null;
  return (
    <div className="prod-side">
      <div className="prod-lbl" style={{ marginTop: 0 }}>Lista de retiro</div>
      {lines.map((line) => {
        const status = LINE_STATUS[line.status] || { label: line.status, cls: '' };
        return (
          <div key={line.id} className={`prod-rline${line.id === currentId ? ' now' : ''}`}>
            <span>{line.id === currentId ? '▶ ' : ''}<b>{line.sku.name}</b></span>
            <span>{`${formatKg(line.pulled)} de ${formatKg(line.planned)} kg`}</span>
            <span className={`prod-chip ${status.cls}`}>{status.label}</span>
          </div>
        );
      })}
      {current && current.pulls.length > 0 && (
        <>
          <div className="prod-lbl">{`Ya retirado de ${current.sku.name}`}</div>
          {current.pulls.map((pull) => (
            <div className="prod-lot" key={pull.id}>
              {`${placeOf(pull)} · ${expiry(pull.expiration_date)} · ${formatKg(pull.quantity)} kg`}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function ProduccionRetiro() {
  const { token, userRole } = useAuth();
  const navigate = useNavigate();
  const { po_number: poNumber } = useParams();
  const wide = useWideLayout();
  const base = `orders/${encodeURIComponent(poNumber)}/retiro`;

  const [state, setState] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [takenBy, setTakenBy] = useState(''); // someone else is retiring this order
  const [step, setStep] = useState('ubicacion');
  const [pieces, setPieces] = useState([]);
  const [verified, setVerified] = useState(false);
  const [panel, setPanel] = useState(null); // 'kg' | 'missing' | 'finish'
  const [kgText, setKgText] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState(null); // {kind: 'error' | 'warn' | 'ok', text, reload}
  const [busy, setBusy] = useState(null); // 'scan' | 'pull' | 'missing' | 'finish'
  const [focusKey, setFocusKey] = useState(0);

  // Async handlers read these, not the render's values: a gun can fire again
  // before React re-renders.
  const stateRef = useRef(null);
  const piecesRef = useRef([]);
  const busyRef = useRef(null);
  const scanQueue = useRef([]);
  const pieceId = useRef(0);
  const requestId = useRef(null);

  const refocus = () => setFocusKey((k) => k + 1);

  const updatePieces = useCallback((next) => {
    piecesRef.current = next;
    // The batch is gone (saved, cleared or another lot): the next one gets a new id.
    if (next.length === 0) requestId.current = null;
    setPieces(next);
  }, []);

  // Every retiro POST answers with the fresh state; it replaces ours.
  const applyState = useCallback((next) => {
    const changed = lotKey(next) !== lotKey(stateRef.current);
    stateRef.current = next;
    setState(next);
    if (changed) {
      setStep('ubicacion');
      setVerified(false);
      setPanel(null);
      scanQueue.current = [];
      updatePieces([]);
    }
  }, [updatePieces]);

  // One person per order: entering the retiro takes it.
  const load = useCallback(async () => {
    setNotice(null);
    const res = await produccionApi.get(`${base}/`, token);
    if (!res.ok) {
      setLoadError(errorMessage(res));
      return;
    }
    let next = res.data;
    if (OPEN_STAGES.includes(next.stage) && !next.mine) {
      const took = await produccionApi.send('POST', `${base}/take/`, {}, token);
      if (!took.ok) {
        if (took.data && took.data.code === 'tomada') setTakenBy(took.data.error);
        else setLoadError(errorMessage(took));
        return;
      }
      next = took.data;
    }
    setLoadError('');
    setTakenBy('');
    applyState(next);
  }, [base, token, applyState]);

  useEffect(() => { load(); }, [load]);

  const fail = (text, reload = false) => {
    setNotice({ kind: 'error', text, reload });
    playTone('error');
    refocus();
  };

  // One request at a time; a second click or Enter while it runs sends nothing.
  const begin = (kind) => {
    if (busyRef.current) return false;
    busyRef.current = kind;
    setBusy(kind);
    setNotice(null);
    return true;
  };

  const end = () => {
    busyRef.current = null;
    setBusy(null);
  };

  const failed = (res) => fail(errorMessage(res), RELOAD_CODES.includes(res.data && res.data.code));

  const post = async (kind, path, body) => {
    if (!begin(kind)) return null;
    const res = await produccionApi.send('POST', `${base}/${path}`, body, token);
    end();
    if (!res.ok) {
      failed(res);
      return null;
    }
    return res;
  };

  const lotBody = () => {
    const { current } = stateRef.current;
    const { target } = current;
    return { line_id: current.line_id, rack: target.rack, shelf: target.shelf, batch: target.batch };
  };

  const addPiece = (code, kg) => {
    pieceId.current += 1;
    if (!requestId.current) requestId.current = newRequestId();
    updatePieces([...piecesRef.current, { id: pieceId.current, code, kg }]);
  };

  const handleLocation = (code) => {
    const { target } = stateRef.current.current;
    const scanned = parseLocation(code);
    if (!scanned || `${scanned.rack}-${scanned.shelf}` !== placeOf(target).toUpperCase()) {
      fail(`Esa no es la ubicación. Ve a ${placeOf(target)}.`);
      return;
    }
    setNotice(null);
    setStep('piezas');
    refocus();
  };

  const checkPiece = async (code) => {
    const current = stateRef.current && stateRef.current.current;
    if (!current || !current.target) return;
    if (piecesRef.current.some((p) => p.code === code)) {
      setNotice({ kind: 'warn', text: DUPLICATE });
      playTone('warning');
      return;
    }
    const key = lotKey(stateRef.current);
    const res = await produccionApi.send('POST', `${base}/scan/`, { ...lotBody(), code }, token);
    if (lotKey(stateRef.current) !== key) return; // the lot changed meanwhile
    if (!res.ok) {
      const text = errorMessage(res);
      fail(text, /Vuelve a cargar/.test(text));
      return;
    }
    setNotice(null);
    if (res.data.kind === 'qr') setVerified(true);
    else addPiece(code, res.data.kg);
  };

  // Labels shot while the previous one is being checked wait their turn.
  const handlePieceScan = (code) => {
    if (busyRef.current && busyRef.current !== 'scan') return;
    scanQueue.current.push(code);
    if (busyRef.current) return;
    busyRef.current = 'scan';
    setBusy('scan');
    (async () => {
      while (scanQueue.current.length) {
        // eslint-disable-next-line no-await-in-loop
        await checkPiece(scanQueue.current.shift());
      }
      busyRef.current = null;
      setBusy(null);
    })();
  };

  const addTyped = () => {
    const kg = parseKg(kgText);
    if (kg === null || kg >= MAX_PIECE_KG) {
      fail(BAD_KG);
      return;
    }
    addPiece(null, kg.toFixed(3));
    setKgText('');
    setNotice(null);
    setPanel(null);
  };

  // The ✕ that had the focus disappears: hand it back to the scan field.
  const removePiece = (id) => {
    updatePieces(piecesRef.current.filter((p) => p.id !== id));
    refocus();
  };

  const pull = async () => {
    const taken = piecesRef.current;
    const quantity = sumKg(taken);
    if (quantity <= 0 || !begin('pull')) return;
    const lineId = stateRef.current.current.line_id;
    const pulledBefore = pulledOf(stateRef.current, lineId);
    const res = await produccionApi.send('POST', `${base}/pull/`, {
      ...lotBody(), quantity, pieces: taken.map(({ code, kg }) => ({ code, kg })), request_id: requestId.current,
    }, token);
    if (res.ok) {
      end();
      applyState(res.data);
      updatePieces([]);
      refocus();
      return;
    }
    if (res.status !== 0 && res.status < 500) {
      end();
      failed(res);
      return;
    }
    // No answer: the pull may have been saved anyway. Look before "Listo" can send it again.
    const check = await produccionApi.get(`${base}/`, token);
    end();
    if (!check.ok) {
      fail(errorMessage(res), true);
      return;
    }
    applyState(check.data);
    if (pulledOf(check.data, lineId) > pulledBefore) {
      updatePieces([]);
      setNotice({ kind: 'ok', text: SAVED });
      refocus();
      return;
    }
    // Not saved: same pieces, same request_id, so pressing "Listo" again is safe.
    fail(errorMessage(res));
  };

  const openPanel = (name) => {
    setNotice(null);
    setReason('');
    setNote('');
    setKgText('');
    setPanel(name);
  };

  const closePanel = () => {
    setPanel(null);
    refocus();
  };

  const reportMissing = async () => {
    if (!reason || piecesRef.current.length) return;
    const res = await post('missing', 'missing/', { ...lotBody(), reason, note: note.trim() });
    if (!res) return;
    applyState(res.data);
    setPanel(null);
  };

  const finishLine = async () => {
    if (piecesRef.current.length) return;
    const res = await post('finish', 'finish-line/', { line_id: stateRef.current.current.line_id });
    if (!res) return;
    applyState(res.data);
    setPanel(null);
  };

  // The frío pickers come from their list of orders.
  const frozen = userRole === 'frozen';
  const back = () => navigate(frozen ? '/picking' : '/production');

  // Lets go of the order (what was saved stays) so someone else can go on.
  const letGo = async () => {
    if (piecesRef.current.length) return;
    const res = await post('release', 'release/', {});
    if (res) back();
  };

  const header = (title, sub, extra = null) => (
    <div className="prod-retiro-head">
      <div>
        <h1 className="prod-title">{title}</h1>
        {sub && <div className="prod-sub" style={{ margin: 0 }}>{sub}</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {extra}
        <button type="button" className="prod-btn ghost prod-btn-small" onClick={back}>← Volver</button>
      </div>
    </div>
  );

  if (takenBy) {
    return (
      <div className="prod-page">
        {header(poNumber)}
        <div className="prod-card amber"><div className="n">{takenBy}</div></div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="prod-page">
        {header(poNumber)}
        <div className="prod-bad prod-error">{loadError}</div>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="prod-page">
        {header(poNumber)}
        <div className="prod-sub">Cargando…</div>
      </div>
    );
  }

  const { lines, current } = state;
  const total = lines.length;
  const done = lines.filter((l) => l.status !== 'pendiente').length;
  const position = current ? lines.findIndex((l) => l.id === current.line_id) + 1 : total;
  const productName = state.product ? state.product.name : '';
  const acting = Boolean(busy) && busy !== 'scan';
  const taken = sumKg(pieces);

  const noticeBox = notice && (
    <>
      <div className={NOTICE_CLASS[notice.kind]}>{notice.text}</div>
      {notice.reload && (
        <button type="button" className="prod-btn ghost prod-btn-small" onClick={load}>Volver a cargar</button>
      )}
    </>
  );

  const confirmButtons = (onConfirm, enabled, kind) => (
    <div className="prod-submit-row">
      <button type="button" className="prod-btn green" disabled={!enabled || Boolean(busy)} onClick={onConfirm}>
        {busy === kind ? 'Guardando…' : 'Confirmar'}
      </button>
      <button type="button" className="prod-btn ghost" disabled={acting} onClick={closePanel}>Cancelar</button>
    </div>
  );

  const unsaved = (target) => pieces.length > 0 && target && (
    <div className="prod-warn">{`Primero presiona "Listo con ${placeOf(target)}" para guardar las piezas que tomaste.`}</div>
  );

  let flow;
  const closedMessage = CLOSED_STAGES[state.stage];
  if (closedMessage || !current) {
    // Every line closed without a gram pulled: there is nothing to close.
    const nothing = !closedMessage && lines.every((l) => Number(l.pulled) === 0);
    const short = closedMessage || nothing ? [] : lines.filter((l) => l.status === 'corta');
    flow = (
      <div className="prod-done-hero">
        <div className={`msg${nothing ? ' amber' : ''}`}>{closedMessage || (nothing ? NOTHING_PULLED : 'Retiro completo ✓')}</div>
        {short.map((l) => (
          <div className="prod-sub" key={l.id}>{`Faltó ${l.sku.name}: ${formatKg(l.pulled)} de ${formatKg(l.planned)} kg`}</div>
        ))}
        {!closedMessage && !nothing && state.stage === 'lista_para_terminar' && (
          <div className="prod-sub">La orden quedó lista para terminar.</div>
        )}
        <button type="button" className="prod-btn blue" onClick={back}>{frozen ? 'Volver a pedidos' : 'Volver a Producción'}</button>
      </div>
    );
  } else {
    const { target, sku } = current;
    const line = lines.find((l) => l.id === current.line_id) || { pulled: '0.000', planned: current.remaining };
    const canFinish = current.pulls.length > 0 || !target;
    // Plan ± 10 %: from `min` the line is done; past `max` it takes another order.
    const inBand = Number(line.pulled) >= Number(line.min);
    const overMax = round3(Number(line.pulled) + taken) > Number(line.max);

    const finishPanel = (
      <div className="prod-panel">
        <div className="prod-panel-q">
          {`¿Terminar ${sku.name} con ${formatKg(line.pulled)} kg de ${formatKg(line.planned)} kg?`}
        </div>
        {unsaved(target)}
        {confirmButtons(finishLine, pieces.length === 0, 'finish')}
      </div>
    );

    if (!target) {
      flow = (
        <>
          <div className="prod-card red">
            <div className="n">{`No queda stock de ${sku.name}.`}</div>
            <div className="d">{`Retirado ${formatKg(line.pulled)} de ${formatKg(line.planned)} kg.`}</div>
          </div>
          {noticeBox}
          {panel === 'finish' ? finishPanel : (
            <button type="button" className="prod-btn blue" disabled={acting} onClick={() => openPanel('finish')}>
              Terminar con lo retirado
            </button>
          )}
        </>
      );
    } else {
      const place = placeOf(target);
      const atShelf = step === 'piezas';

      const goto = (
        <div className="prod-goto">
          <small>{atShelf ? 'Estás en' : 'Ve a'}</small>
          <div className="big">{atShelf ? `${place} ✓` : place}</div>
          {atShelf ? (
            <>
              <div className="what">{`${sku.name} · faltan ${formatKg(current.remaining)} kg`}</div>
              <div className={`prod-verify${verified ? ' ok' : ''}`}>
                {verified ? 'Lote verificado ✓' : `Verifica: ${expiry(target.expiration_date)}`}
              </div>
            </>
          ) : (
            <>
              <div className="what">{sku.name}</div>
              <div className="what dim">
                {target.expiration_date ? `lote que vence ${formatDate(target.expiration_date)}` : 'lote sin vencimiento'}
              </div>
              <span className="take">{`Toma ${formatKg(target.take)} kg`}</span>
            </>
          )}
        </div>
      );

      const missingPanel = (
        <div className="prod-panel">
          <div className="prod-panel-q">{`No está / no alcanza en ${place}`}</div>
          <div className="prod-reasons">
            {REASONS.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`prod-reason${reason === r.key ? ' on' : ''}`}
                aria-pressed={reason === r.key}
                onClick={() => setReason(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <label className="prod-lbl" style={{ display: 'block' }}>
            Nota (opcional)
            <textarea className="prod-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          {unsaved(target)}
          {confirmButtons(reportMissing, Boolean(reason) && pieces.length === 0, 'missing')}
        </div>
      );

      const kgPanel = (
        <div className="prod-kg-row">
          <input
            className="prod-input"
            inputMode="decimal"
            aria-label="Kg de la pieza"
            placeholder="kg, por ejemplo 4,2"
            autoFocus
            value={kgText}
            onChange={(e) => setKgText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              addTyped();
            }}
          />
          <button type="button" className="prod-btn blue" onClick={addTyped}>Agregar</button>
          <button type="button" className="prod-btn ghost" onClick={closePanel}>Cancelar</button>
        </div>
      );

      const piecesBox = (
        <div className="prod-pieces">
          {pieces.map((piece, index) => (
            <div className="prod-piece" key={piece.id}>
              <span>{`Pieza ${index + 1} · ${formatKg(piece.kg)} kg${piece.code ? '' : ' (tipeado)'}`}</span>
              <button
                type="button"
                className="prod-piece-x"
                aria-label={`Quitar pieza ${index + 1}`}
                disabled={acting}
                onClick={() => removePiece(piece.id)}
              >
                ✕
              </button>
            </div>
          ))}
          <div className="prod-pieces-total">
            <span>{`Retirado de ${place}:`}</span>
            <span>{` ${formatKg(taken)} kg`}</span>
          </div>
        </div>
      );

      const actions = (
        <div className="prod-actions">
          {atShelf && (
            <button type="button" className="prod-link" disabled={acting} onClick={() => openPanel('kg')}>
              Tipear kg (etiqueta no se lee)
            </button>
          )}
          <button type="button" className="prod-link red" disabled={Boolean(busy)} onClick={() => openPanel('missing')}>
            No está / no alcanza
          </button>
          {canFinish && !inBand && (
            <button type="button" className="prod-link" disabled={Boolean(busy)} onClick={() => openPanel('finish')}>
              Terminar con lo retirado
            </button>
          )}
        </div>
      );

      const overMaxWarn = overMax && (
        <div className="prod-warn">
          {`No puedes retirar más de ${formatKg(line.max)} kg de ${sku.name} (lo planificado + 10 %). Quita una pieza. `
            + 'Si se necesita más materia prima, hay que planificar otra orden de producción.'}
        </div>
      );

      const finishNow = inBand && pieces.length === 0 && (
        <button type="button" className="prod-btn green" disabled={Boolean(busy)} onClick={finishLine}>
          {busy === 'finish' ? 'Guardando…' : `Terminar con ${formatKg(line.pulled)} kg ✓`}
        </button>
      );

      let below;
      if (panel === 'missing') below = missingPanel;
      else if (panel === 'finish') below = finishPanel;
      else {
        below = (
          <>
            {panel === 'kg' ? kgPanel : (
              <ProduccionScanInput
                key={step}
                placeholder={atShelf ? 'Escanea la etiqueta de la pieza' : 'Escanea la ubicación'}
                onScan={atShelf ? handlePieceScan : handleLocation}
                disabled={acting}
                focusKey={focusKey}
              />
            )}
            {noticeBox}
            {atShelf && piecesBox}
            {atShelf && overMaxWarn}
            {atShelf && (
              <button
                type="button"
                className="prod-btn green"
                disabled={taken <= 0 || overMax || Boolean(busy)}
                onClick={pull}
              >
                {`Listo con ${place}`}
              </button>
            )}
            {finishNow}
            {actions}
          </>
        );
      }

      flow = (
        <>
          {goto}
          {(panel === 'missing' || panel === 'finish') && noticeBox}
          {below}
        </>
      );
    }
  }

  const canLetGo = state.mine && OPEN_STAGES.includes(state.stage);
  const letGoButton = canLetGo && (
    <button
      type="button"
      className="prod-btn ghost prod-btn-small"
      disabled={pieces.length > 0 || Boolean(busy)}
      onClick={letGo}
    >
      Soltar orden
    </button>
  );

  return (
    <div className="prod-page">
      {header(`${state.po_number} · ${position} de ${total}`, productName, letGoButton)}
      {canLetGo && pieces.length > 0 && (
        <div className="prod-sub">Guarda o borra las piezas escaneadas antes de soltar.</div>
      )}
      <div className="prod-prog"><i style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
      {wide ? (
        <div className="prod-split">
          <div>{flow}</div>
          <RetiroPanel state={state} />
        </div>
      ) : flow}
    </div>
  );
}

export default ProduccionRetiro;
